import React, { useState, useEffect, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService, collegeJourneyProgressService } from '../../lib/college-journey-service';
import { collegeAccessControlService } from '../../lib/college-access-control-service';
import { fixCollegeAuthentication } from '../../utils/fixCollegeAuthentication';
// import AIDataImporter from './AIDataImporter'; // Archived
// import DataFieldExporter from './DataFieldExporter'; // Archived
// import DebugPanel from './DebugPanel'; // Archived
import { 
  Users, 
  User, 
  GraduationCap, 
  MapPin,
  Calendar,
  Target,
  BookOpen,
  Brain,
  Plus,
  Save,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  Eye,
  EyeOff,
  Edit3,
  Trash2,
  TrendingUp,
  FileText,
  DollarSign,
  Sparkles,
  Upload,
  Download,
  Settings,
  Star,
  Shield,
  UserCheck,
  Database,
  ChevronDown,
  MoreHorizontal,
  Menu,
  X
} from 'lucide-react';

const ParentCollegeHub = () => {
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { colleges: contextColleges, updateAcademicProfile, addCollege } = useCollegeContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Module visibility state - now stored in database
  const [moduleAccess, setModuleAccess] = useState({});
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);
  const [accessError, setAccessError] = useState(null);

  // Load access settings from database on component mount
  useEffect(() => {
    const loadAccessSettings = async () => {
      if (!family?.id) return;
      
      try {
        setIsLoadingAccess(true);
        setAccessError(null);
        
        console.log('🔍 Loading access settings for family:', family.id);
        const accessMap = await collegeAccessControlService.getAccessMap(family.id);
        console.log('🔍 Raw accessMap from database:', accessMap);
        
        // Get all family settings to see what's in the database
        const allSettings = await collegeAccessControlService.getFamilySettings(family.id);
        console.log('🔍 All family access settings in database:', allSettings);
        
        // Ensure all children have explicit access settings, defaulting to true for missing entries
        const allChildrenIds = family.kids?.map(kid => kid.id) || [];
        const completeAccessMap = {};
        
        allChildrenIds.forEach(childId => {
          // If child has explicit setting, use it; otherwise default to true
          completeAccessMap[childId] = accessMap.hasOwnProperty(childId) ? accessMap[childId] : true;
        });
        
        console.log('🔍 Complete accessMap with defaults:', completeAccessMap);
        setModuleAccess(completeAccessMap);
      } catch (error) {
        console.error('Failed to load access settings:', error);
        setAccessError('Failed to load access settings');
        // Fallback to empty object to allow default access
        setModuleAccess({});
      } finally {
        setIsLoadingAccess(false);
      }
    };

    loadAccessSettings();
  }, [family?.id]);

  // Memoize all children computation (regardless of access)
  const allChildren = useMemo(() => {
    // Get all children regardless of access
    // Fallback to family.kids if familyUsers is empty (timing issue)
    let children = familyUsers?.filter(user => user.type !== 'parent') || [];
    
    // If familyUsers is empty but we have family.kids, use that
    if (children.length === 0 && family?.kids?.length > 0) {
      children = family.kids.map(kid => ({ 
        ...kid, 
        type: 'student', 
        displayName: kid.name || kid.displayName,
        // Ensure we have the auth_user_id field for profile operations
        auth_user_id: kid.authUserId || kid.auth_user_id,
      }));
    }
    
    return children;
  }, [familyUsers, family?.kids, family?.id]);

  // Memoize filtered children (only those with access)
  const children = useMemo(() => {
    return allChildren.filter(child => moduleAccess[child.id] !== false); // Default to true
  }, [allChildren, moduleAccess]);
  
  // Save module access when it changes
  const updateModuleAccess = async (childId, hasAccess) => {
    if (!family?.id) {
      console.error('No family ID available for updating access');
      return;
    }

    try {
      // Optimistically update local state
      const newAccess = { ...moduleAccess, [childId]: hasAccess };
      setModuleAccess(newAccess);
      
      // Save to database
      await collegeAccessControlService.setCollegeHubAccess(family.id, childId, hasAccess);
      
      console.log(`✅ Updated college access for child ${childId}: ${hasAccess}`);
    } catch (error) {
      console.error('Failed to update access settings:', error);
      // Revert local state on error
      setModuleAccess(moduleAccess);
      setAccessError('Failed to update access settings. Please try again.');
    }
  };

  // Check if the selected child has started onboarding (child-specific)
  const getChildOnboardingStatus = (child) => {
    if (!child) return false;
    
    const childId = child.id || child.auth_user_id || child.user_id;
    
    // PRIORITY 1: Check localStorage for explicit onboarding progress (most reliable)
    const hasLocalStorageProgress = 
      localStorage.getItem(`student-onboarding-progress-${childId}`) ||
      localStorage.getItem('student-onboarding-progress');
    
    if (hasLocalStorageProgress) {
      console.log('🔍 Child has localStorage onboarding progress:', child.name, hasLocalStorageProgress);
      return true;
    }
    
    // PRIORITY 2: If no localStorage progress, assume they haven't started onboarding
    // (College data alone doesn't indicate onboarding status - it could be parent-added colleges)
    console.log('🔍 No onboarding progress found for:', child.name, 'treating as not onboarded');
    return false;
  };

  // Check for tab query parameter
  const urlParams = new URLSearchParams(location.search);
  const tabParam = urlParams.get('tab');
  
  const [selectedChild, setSelectedChild] = useState(null);
  
  // Debug logging for selectedChild changes
  useEffect(() => {
    console.log('🔍 ParentCollegeHub: selectedChild changed to:', selectedChild?.name || selectedChild?.displayName || 'null', selectedChild ? `(ID: ${selectedChild.id})` : '');
  }, [selectedChild]);

  // For parent view, we should filter family-wide data by selected child rather than reload
  // This avoids infinite loops and performance issues
  const selectedChildColleges = useMemo(() => {
    if (!selectedChild || !contextColleges) return [];
    
    // Try multiple ID fields to find colleges for this child
    const possibleUserIds = [
      selectedChild.auth_user_id,
      selectedChild.authUserId, 
      selectedChild.user_id,
      selectedChild.id
    ].filter(Boolean);
    
    const filtered = contextColleges.filter(college => 
      possibleUserIds.includes(college.user_id) ||
      possibleUserIds.includes(college.suggested_by)
    );
    
    console.log('🔍 ParentCollegeHub: Filtering colleges for child', selectedChild.name);
    console.log('🔍 ParentCollegeHub: Possible user IDs:', possibleUserIds);
    console.log('🔍 ParentCollegeHub: Total colleges:', contextColleges.length, 'Filtered for child:', filtered.length);
    
    if (contextColleges.length > 0) {
      console.log('🔍 ParentCollegeHub: Sample college user_ids:', contextColleges.slice(0, 3).map(c => ({
        name: c.name,
        user_id: c.user_id,
        suggested_by: c.suggested_by
      })));
    }
    
    return filtered;
  }, [selectedChild, contextColleges]);
  
  // State to track database onboarding status
  const [databaseOnboardingStatus, setDatabaseOnboardingStatus] = useState({});

  // Load onboarding status from database when children change
  useEffect(() => {
    const loadOnboardingStatuses = async () => {
      if (!children || children.length === 0) return;
      
      const statuses = {};
      for (const child of children) {
        try {
          const progress = await collegeJourneyProgressService.getProgress(child.id);
          statuses[child.id] = progress?.onboarding_complete || false;
        } catch (error) {
          console.error('Error loading onboarding status for', child.name, ':', error);
          statuses[child.id] = false;
        }
      }
      setDatabaseOnboardingStatus(statuses);
    };

    loadOnboardingStatuses();
  }, [children]);

  // Calculate onboarding status dynamically when selectedChild changes
  const hasSelectedChildStartedOnboarding = useMemo(() => {
    if (!selectedChild) return false;
    
    // Check database first, then fall back to localStorage
    const dbStatus = databaseOnboardingStatus[selectedChild.id];
    if (dbStatus !== undefined) {
      return dbStatus;
    }
    
    // Fallback to old logic
    return getChildOnboardingStatus(selectedChild);
  }, [selectedChild, databaseOnboardingStatus]);
  
  // Default to appropriate first tab based on onboarding status
  const [activeTab, setActiveTab] = useState(() => {
    if (tabParam) return tabParam;
    return 'overview'; // Start with overview, will be updated when selectedChild is set
  });
  const [parentColleges, setParentColleges] = useState([]);
  const [studentColleges, setStudentColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingCollege, setAddingCollege] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [showKidView, setShowKidView] = useState(false);
  const [showAccessControl, setShowAccessControl] = useState(false);
  const [showAIPromptGen, setShowAIPromptGen] = useState(false);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [showMoreTools, setShowMoreTools] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Pre-onboarding tabs: Setup workflow in logical order
  const preOnboardingTabs = [
    { id: 'overview', label: 'Setup Overview', icon: Users },
    { id: 'colleges', label: 'College Management', icon: GraduationCap },
    { id: 'strategic-assessment', label: '6-Phase Strategic Assessment', icon: Brain },
    { id: 'testing', label: 'Testing Hub', icon: Settings }
  ];

  // Post-onboarding tabs: Ongoing workflow priorities
  const postOnboardingTabs = [
    { id: 'colleges', label: 'College Management', icon: GraduationCap },
    { id: 'timeline', label: 'Timeline & Deadlines', icon: Calendar },
    { id: 'collaboration', label: 'Parent-Student Notes', icon: FileText },
    { id: 'insights', label: 'Analytics', icon: TrendingUp },
  ];

  // Setup tools that are accessible via More Tools after onboarding
  const setupTools = [
    { id: 'overview', label: 'Setup Overview', icon: Users },
    { id: 'strategic-assessment', label: '6-Phase Strategic Assessment', icon: Brain },
    { id: 'testing', label: 'Testing Hub', icon: Settings }
  ];

  const tabs = hasSelectedChildStartedOnboarding ? postOnboardingTabs : preOnboardingTabs;

  useEffect(() => {
    // Only proceed if we have children data AND access control data is loaded
    if (children.length > 0 && !selectedChild && !isLoadingAccess) {
      console.log('🔍 ParentCollegeHub: Setting selected child from children:', children);
      console.log('🔍 ParentCollegeHub: Children names:', children.map(c => c.name || c.displayName));
      
      // First, try to use the saved child from localStorage if they have access
      const savedChildId = localStorage.getItem('college-selected-student-id');
      console.log('🔍 ParentCollegeHub: Saved child ID from localStorage:', savedChildId);
      let childToSelect = null;
      
      if (savedChildId) {
        // Check if the saved child still has access and exists in current children array
        // Note: savedChildId is now an auth_user_id, so we need to match against that
        childToSelect = children.find(child => 
          (child.auth_user_id || child.authUserId) === savedChildId
        );
        if (childToSelect) {
          console.log('🔍 ParentCollegeHub: Using saved child from localStorage:', childToSelect.name || childToSelect.displayName);
        } else {
          console.log('🔍 ParentCollegeHub: Saved child not found or no longer has access, clearing localStorage and selecting first available child');
          console.log('🔍 ParentCollegeHub: Available children IDs:', children.map(c => c.id));
          // Clear invalid localStorage entry
          localStorage.removeItem('college-selected-student-id');
        }
      }
      
      // If no valid saved child, select the first child with access
      if (!childToSelect && children.length > 0) {
        childToSelect = children[0];
        console.log('🔍 ParentCollegeHub: Selected first available child:', childToSelect.name || childToSelect.displayName, 'with ID:', childToSelect.id);
      }
      
      if (childToSelect) {
        console.log('🔍 ParentCollegeHub: Setting selectedChild to:', childToSelect.name || childToSelect.displayName);
        setSelectedChild(childToSelect);
        // Save to localStorage so other components can access it - use auth_user_id
        const authUserId = childToSelect.auth_user_id || childToSelect.authUserId;
        if (authUserId) {
          localStorage.setItem('college-selected-student-id', authUserId);
          localStorage.setItem('college-selected-student-name', childToSelect.name || childToSelect.displayName || 'Student');
          console.log('🔍 ParentCollegeHub: Auto-selected child auth_user_id:', authUserId);
        } else {
          console.error('❌ ParentCollegeHub: No auth_user_id found for auto-selected child:', childToSelect);
        }
      }
    }
  }, [children, selectedChild, isLoadingAccess]);

  useEffect(() => {
    if (selectedChild) {
      loadCollegeData();
      
      // Update active tab when child selection changes if current tab isn't available
      const availableTabIds = hasSelectedChildStartedOnboarding ? 
        postOnboardingTabs.map(tab => tab.id) : 
        preOnboardingTabs.map(tab => tab.id);
      
      if (!availableTabIds.includes(activeTab)) {
        setActiveTab(hasSelectedChildStartedOnboarding ? 'colleges' : 'overview');
      }
    }
  }, [selectedChild, hasSelectedChildStartedOnboarding]);

  // Set initial tab based on onboarding status when hasSelectedChildStartedOnboarding is first calculated
  useEffect(() => {
    if (selectedChild && !tabParam) {
      setActiveTab(hasSelectedChildStartedOnboarding ? 'colleges' : 'overview');
    }
  }, [hasSelectedChildStartedOnboarding, selectedChild, tabParam]);

  // Close More Tools dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreTools && !event.target.closest('.more-tools-dropdown')) {
        setShowMoreTools(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreTools]);

  // Manual fix for migration issues
  const fixParentSuggestions = async () => {
    if (!selectedChild) {
      setSaveStatus('error');
      setSaveMessage('No student selected');
      setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 3000);
      return;
    }
    
    try {
      console.log('🔧 ParentCollegeHub: Attempting to fix parent suggestions...');
      console.log('🔧 ParentCollegeHub: Selected child:', selectedChild);
      console.log('🔧 ParentCollegeHub: Child ID:', selectedChild.id, 'Type:', typeof selectedChild.id);
      
      // Try different possible ID field names for the child
      const childId = selectedChild.id || selectedChild.auth_user_id || selectedChild.user_id;
      const familyId = family?.id;
      
      console.log('🔧 ParentCollegeHub: Resolved child ID:', childId);
      console.log('🔧 ParentCollegeHub: Resolved family ID:', familyId);
      
      // Validate IDs (single-family architecture)
      if (!childId || childId === 'undefined') {
        setSaveStatus('error');
        setSaveMessage('Invalid student ID. Please refresh the page and try again.');
        setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 5000);
        return;
      }
      
      setSaveMessage('Fixing parent suggestions...');
      setSaveStatus('saving');
      
      // Load parent suggestions - first try with child ID, then without
      let parentSuggestions = await collegeJourneyService.getParentSuggestions(familyId, childId);
      console.log('🔧 ParentCollegeHub: Found parent suggestions (with child ID):', parentSuggestions);
      
      if (parentSuggestions.length === 0) {
        // Try without child ID to get all family suggestions
        parentSuggestions = await collegeJourneyService.getParentSuggestions(familyId);
        console.log('🔧 ParentCollegeHub: Found parent suggestions (family-wide):', parentSuggestions);
      }
      
      if (parentSuggestions.length === 0) {
        setSaveStatus('error');
        setSaveMessage('No parent suggestions found to fix. Try running the migration first.');
        setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 5000);
        return;
      }
      
      let updatedCount = 0;
      let errorCount = 0;
      
      // For each parent suggestion with a source_college_id, update the college record
      for (const suggestion of parentSuggestions) {
        if (suggestion.source_college_id) {
          console.log(`🔧 ParentCollegeHub: Updating college ${suggestion.source_college_id} for suggestion ${suggestion.title}`);
          
          const { error } = await supabase
            .from('college_journey_colleges')
            .update({
              suggestion_source: 'parent',
              suggested_by: suggestion.parent_user_id,
              parent_suggestion_id: suggestion.id
            })
            .eq('id', suggestion.source_college_id);
          
          if (error) {
            console.error('🔧 ParentCollegeHub: Error updating college:', error);
            errorCount++;
          } else {
            console.log(`✅ ParentCollegeHub: Successfully updated college ${suggestion.source_college_id}`);
            updatedCount++;
          }
        }
      }
      
      // Reload data after fixing
      await loadCollegeData();
      
      // Show success message
      setSaveStatus('success');
      setSaveMessage(`Fixed ${updatedCount} parent suggestions${errorCount > 0 ? ` (${errorCount} errors)` : ''}`);
      setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 5000);
      
    } catch (error) {
      console.error('❌ ParentCollegeHub: Error fixing parent suggestions:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to fix parent suggestions: ' + error.message);
      setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 5000);
    }
  };

  const loadCollegeData = async () => {
    if (!selectedChild) return;
    
    setLoading(true);
    try {
      // First, fix authentication if needed
      console.log('🔧 ParentCollegeHub: Checking authentication...');
      const authFix = await fixCollegeAuthentication();
      if (authFix.success && authFix.fixed) {
        console.log('✅ ParentCollegeHub: Authentication fixed:', authFix.message);
      } else if (!authFix.success) {
        console.error('❌ ParentCollegeHub: Authentication fix failed:', authFix.error);
      }
      
      console.log('🔍 ParentCollegeHub: Loading data for child:', selectedChild);
      
      // Try different possible ID field names
      const childId = selectedChild.id || selectedChild.auth_user_id || selectedChild.user_id;
      const familyId = family?.id;
      
      console.log('🔍 ParentCollegeHub: Resolved childId:', childId, 'familyId:', familyId);
      console.log('🔍 ParentCollegeHub: Family object:', family);
      
      if (!childId) {
        console.error('❌ ParentCollegeHub: Invalid child ID');
        console.error('❌ ParentCollegeHub: childId:', childId);
        setLoading(false);
        return;
      }
      
      // Load parent suggestions and student colleges (single-family architecture)
      const { colleges } = await collegeJourneyService.getFamilyCollegeJourneyData();
      console.log('🔍 ParentCollegeHub: All colleges loaded:', colleges);
      console.log('🔍 ParentCollegeHub: College suggestion sources:', colleges.map(c => ({ name: c.name, source: c.suggestion_source, addedBy: c.suggested_by })));
      
      // Also try to load parent suggestions directly
      const parentSuggestions = await collegeJourneyService.getParentSuggestions(childId);
      console.log('🔍 ParentCollegeHub: Direct parent suggestions:', parentSuggestions);
      
      const parentSuggested = colleges.filter(c => c.suggestion_source === 'parent');
      const studentAdded = colleges.filter(c => c.suggestion_source === 'student' || !c.suggestion_source);
      
      console.log('🔍 ParentCollegeHub: Parent suggested colleges:', parentSuggested);
      console.log('🔍 ParentCollegeHub: Student added colleges:', studentAdded);
      
      // If no parent-suggested colleges found, try to load them via parent suggestions
      if (parentSuggested.length === 0 && parentSuggestions.length > 0) {
        console.log('🔍 ParentCollegeHub: No parent-suggested colleges found, loading via parent suggestions...');
        
        // Load colleges that are linked to parent suggestions
        const collegeIds = parentSuggestions
          .filter(ps => ps.source_college_id)
          .map(ps => ps.source_college_id);
        
        if (collegeIds.length > 0) {
          console.log('🔍 ParentCollegeHub: Found college IDs from parent suggestions:', collegeIds);
          
          // Load the actual college records
          const parentSuggestedColleges = colleges.filter(c => collegeIds.includes(c.id));
          console.log('🔍 ParentCollegeHub: Loaded parent-suggested colleges via IDs:', parentSuggestedColleges);
          
          setParentColleges(parentSuggestedColleges);
        } else {
          setParentColleges([]);
        }
      } else {
        setParentColleges(parentSuggested);
      }
      
      setStudentColleges(studentAdded);
    } catch (error) {
      console.error('❌ ParentCollegeHub: Error loading college data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is actually a parent
  const isParent = currentUser?.isParent || currentUser?.type === 'parent';
  
  if (!isParent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center max-w-md">
          <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Parent Access Required</h2>
          <p className="text-slate-600">This page is only accessible to parents.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Parent-specific controls moved to top */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Mobile menu button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            aria-label="Toggle navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
              {/* Kid View Toggle */}
              <button
                onClick={() => setShowKidView(!showKidView)}
                className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[44px] ${
                  showKidView 
                    ? 'bg-azure-50 text-azure-700 border border-azure-200' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
                title="Toggle between parent and student perspective"
              >
                {showKidView ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="hidden sm:inline">{showKidView ? 'Kid View' : 'Parent View'}</span>
              </button>

              {/* Debug Tools - Hidden on mobile */}
              <button
                onClick={() => setShowDebugModal(true)}
                className="hidden md:flex text-azure-600 hover:text-azure-700 p-2 rounded-lg hover:bg-azure-50 min-h-[44px] items-center justify-center"
                title="Debug Tools & Data"
              >
                <Search className="w-5 h-5" />
              </button>

          {/* Fix Parent Suggestions Debug Button - Hidden on mobile */}
          <button
            onClick={fixParentSuggestions}
            className="hidden md:flex text-orange-600 hover:text-orange-700 p-2 rounded-lg hover:bg-orange-50 min-h-[44px] items-center justify-center"
            title="Fix Parent Suggestions (Debug)"
          >
            <AlertCircle className="w-5 h-5" />
          </button>

          {/* System Status - Hidden on mobile */}
          <button
            onClick={() => navigate('/college/status')}
            className="hidden md:flex text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100 min-h-[44px] items-center justify-center"
            title="System Status"
          >
            <TrendingUp className="w-5 h-5" />
          </button>

          <div className={`rounded-lg px-2 md:px-4 py-2 ${
            showKidView 
              ? 'bg-azure-50 border border-azure-200' 
              : 'bg-violet-50 border border-violet-200'
          }`}>
            <span className={`text-xs md:text-sm font-medium ${
              showKidView ? 'text-azure-700' : 'text-violet-700'
            }`}>
              {showKidView ? 'Student' : 'Parent'}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Status Message */}
        {saveStatus && (
          <div className={`mb-6 p-3 rounded-lg flex items-center gap-2 ${
            saveStatus === 'success' ? 'bg-emerald-50 border border-emerald-200' :
            saveStatus === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-azure-50 border border-azure-200'
          }`}>
            {saveStatus === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
            {saveStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
            {saveStatus === 'saving' && <Clock className="w-5 h-5 text-azure-600 animate-spin" />}
            <span className={
              saveStatus === 'success' ? 'text-emerald-700' :
              saveStatus === 'error' ? 'text-red-700' :
              'text-azure-700'
            }>
              {saveMessage}
            </span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative">
          {/* Mobile Navigation Overlay */}
          {showMobileMenu && (
            <div 
              className="lg:hidden fixed inset-0 z-40 bg-slate-900 bg-opacity-50"
              onClick={() => setShowMobileMenu(false)}
            />
          )}

          {/* Sidebar Navigation */}
          <div className={`
            lg:w-64 lg:flex-shrink-0
            ${showMobileMenu 
              ? 'fixed inset-y-0 left-0 z-50 w-80 transform translate-x-0' 
              : 'fixed inset-y-0 left-0 z-50 w-80 transform -translate-x-full'
            } lg:relative lg:transform-none lg:z-auto lg:bg-transparent
            transition-transform duration-300 ease-in-out lg:transition-none
            bg-white lg:bg-transparent shadow-xl lg:shadow-none
          `}>
            {/* Mobile menu header */}
            <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Navigation</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 lg:p-0">
              <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition-colors min-h-[44px] ${
                      activeTab === tab.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm md:text-base">{tab.label}</span>
                  </button>
                );
              })}
              
              {/* More Tools Dropdown for Post-Onboarding */}
              {hasSelectedChildStartedOnboarding && (
                <div className="relative mt-2 more-tools-dropdown">
                  <button
                    onClick={() => setShowMoreTools(!showMoreTools)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-left font-medium transition-colors bg-white text-slate-700 hover:bg-slate-50 border border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <MoreHorizontal className="w-5 h-5" />
                      Setup Tools
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showMoreTools ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showMoreTools && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                      {setupTools.map((tool) => {
                        const Icon = tool.icon;
                        return (
                          <button
                            key={tool.id}
                            onClick={() => {
                              setActiveTab(tool.id);
                              setShowMoreTools(false);
                              setShowMobileMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left font-medium transition-colors text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg min-h-[44px]"
                          >
                            <Icon className="w-5 h-5" />
                            {tool.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Child Selector */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Managing for:
              </label>
              
              {/* Debug Information */}
              <div className="mb-2 text-xs text-slate-500">
                familyUsers: {familyUsers?.length || 0}, allChildren: {allChildren.length}, children: {children.length}
              </div>
              
              {children.length > 0 ? (
                <select
                  value={selectedChild?.id || ''}
                  onChange={(e) => {
                    const child = children.find(c => c.id === e.target.value);
                    console.log('🔍 ParentCollegeHub: Child selected:', child);
                    setSelectedChild(child);
                    setShowMobileMenu(false);
                    // Save to localStorage so other components can access it
                    if (child) {
                      // Store the auth_user_id which is what the database uses
                      const authUserId = child.auth_user_id || child.authUserId;
                      console.log('🔍 ParentCollegeHub: Storing auth_user_id:', authUserId, 'for child:', child.displayName || child.name);
                      
                      if (authUserId) {
                        localStorage.setItem('college-selected-student-id', authUserId);
                        localStorage.setItem('college-selected-student-name', child.displayName || child.name || child.first_name || 'Student');
                        
                        // Also trigger a storage event for same-window communication
                        window.dispatchEvent(new StorageEvent('storage', {
                          key: 'college-selected-student-id',
                          newValue: authUserId,
                          oldValue: selectedChild?.auth_user_id || selectedChild?.authUserId || null
                        }));
                      } else {
                        console.error('❌ ParentCollegeHub: No auth_user_id found for child:', child);
                      }
                    }
                  }}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-sm md:text-base min-h-[44px]"
                >
                  {!selectedChild && <option value="">Select a child...</option>}
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.displayName || child.name || child.first_name || `Child ${child.id?.slice(-4)}`}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-2">
                    {allChildren.length > 0 
                      ? `All ${allChildren.length} children have module access disabled.`
                      : familyUsers === null || familyUsers === undefined
                        ? `Loading family data...`
                        : `No children found in your family.`}
                  </p>
                  {allChildren.length > 0 && (
                    <button
                      onClick={() => setActiveTab('access')}
                      className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                    >
                      Go to Access Control →
                    </button>
                  )}
                  {familyUsers === null && (
                    <p className="text-xs text-amber-600 mt-1">
                      If this persists, try refreshing the page.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            {selectedChild && (
              <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Colleges</span>
                    <span className="font-medium">{parentColleges.length + studentColleges.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Parent Added</span>
                    <span className="font-medium text-azure-600">{parentColleges.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Student Added</span>
                    <span className="font-medium text-emerald-600">{studentColleges.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Applications</span>
                    <span className="font-medium">0 started</span>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 lg:ml-0">
            {activeTab === 'overview' && <OverviewTab onTabChange={setActiveTab} />}
            {activeTab === 'strategic-assessment' && <StrategicAssessmentTab selectedChild={selectedChild} />}
            {activeTab === 'colleges' && (
              <CollegeManagementTab 
                selectedChild={selectedChild}
                parentColleges={parentColleges}
                studentColleges={studentColleges}
                loading={loading}
                onReload={loadCollegeData}
                setShowAIPromptGen={setShowAIPromptGen}
                selectedColleges={selectedColleges}
                setSelectedColleges={setSelectedColleges}
              />
            )}

            {activeTab === 'timeline' && <TimelineTab selectedChild={selectedChild} />}
            {activeTab === 'insights' && <InsightsTab selectedChild={selectedChild} />}
            {activeTab === 'collaboration' && <CollaborationTab selectedChild={selectedChild} showKidView={showKidView} />}
            {activeTab === 'testing' && <TestingTab />}
            {activeTab === 'access' && (
              <AccessControlTab 
                allChildren={allChildren}
                moduleAccess={moduleAccess}
                updateModuleAccess={updateModuleAccess}
                isLoadingAccess={isLoadingAccess}
                accessError={accessError}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* AI Prompt Generator Modal - Archived */}
      {/* {showAIPromptGen && (
        <DataFieldExporter
          colleges={parentColleges}
          selectedColleges={selectedColleges}
          onClose={() => setShowAIPromptGen(false)}
        />
      )} */}
      
      {/* Debug Modal */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Debug Tools</h3>
            {/* <DebugPanel /> Archived */}
            <button
              onClick={() => setShowDebugModal(false)}
              className="mt-4 w-full bg-slate-600 text-white px-4 py-3 rounded-lg hover:bg-slate-700 min-h-[44px] text-sm md:text-base font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// College Management Tab (from ParentCollegeManager)
// Strategic Assessment Tab Component
const StrategicAssessmentTab = ({ selectedChild }) => {
  const navigate = useNavigate();
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  
  useEffect(() => {
    if (selectedChild) {
      checkAssessmentStatus();
    }
  }, [selectedChild]);

  const checkAssessmentStatus = async () => {
    if (!selectedChild) return;
    
    try {
      const { data: profileData } = await supabase
        .from('student_academic_profiles')
        .select('strategic_context_summary, initial_question_set, dynamic_question_guidance, synthesis_instructions, assessment_phase')
        .eq('user_id', selectedChild.id)
        .single();
      
      if (profileData) {
        setAssessmentStatus({
          hasStrategicContext: !!profileData.strategic_context_summary,
          hasQuestionSet: !!profileData.initial_question_set?.length,
          hasGuidance: !!profileData.dynamic_question_guidance && !!profileData.synthesis_instructions,
          phase: profileData.assessment_phase || 1
        });
      }
    } catch (error) {
      console.error('Error checking assessment status:', error);
    }
  };

  if (!selectedChild) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Brain className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Student Selected</h3>
        <p className="text-slate-600">Please select a student to set up their strategic assessment.</p>
      </div>
    );
  }

  const assessmentSteps = [
    {
      id: 1,
      title: 'Complete Student & Parent Intelligence',
      description: 'Comprehensive student profile, parent observations, personality assessment, and academic focus selection',
      route: '/college/comprehensive-student-profile',
      completed: assessmentStatus?.step_1_completed
    },
    {
      id: 2,
      title: 'Strategic Context Generation',
      description: 'Generate strategic positioning using all Step 1 intelligence',
      route: '/college/student-context-generator',
      completed: assessmentStatus?.phase_2_completed
    },
    {
      id: 3,
      title: 'Personalized Question Set',
      description: 'Create assessment questions tailored to student personality and academic focus',
      route: '/college/question-set-generator',
      completed: assessmentStatus?.hasQuestionSet
    },
    {
      id: 4,
      title: 'Dynamic Framework',
      description: 'Create intelligent guidance for follow-up questions and synthesis',
      route: '/college/dynamic-guidance-generator',
      completed: assessmentStatus?.hasGuidance
    },
    {
      id: 5,
      title: 'Synthesis Instructions',
      description: 'Create customized instructions for generating strategic insights',
      route: '/college/synthesis-instructions',
      completed: assessmentStatus?.phase_5_completed
    },
    {
      id: 6,
      title: 'Format Customization',
      description: 'Customize language, tone, and structure of final insights output',
      route: '/college/formatting-customizer',
      completed: assessmentStatus?.phase_6_completed
    }
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-8">
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
          Streamlined Strategic Assessment
        </h2>
        <p className="text-slate-600 text-lg">
          Create a personalized college assessment system for <strong>{selectedChild.displayName || selectedChild.name}</strong> using 
          comprehensive student intelligence, personality insights, and academic focus to generate targeted assessment strategies.
        </p>
      </div>

      <div className="space-y-6">
        {assessmentSteps.map((phase, index) => (
          <div key={phase.id} className="border border-slate-200 rounded-lg p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                  phase.completed ? 'bg-emerald-500' : index === 0 ? 'bg-slate-900' : 'bg-slate-300'
                }`}>
                  {phase.completed ? <CheckCircle className="w-5 h-5" /> : phase.id}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Step {phase.id}: {phase.title}
                  </h3>
                  <p className="text-slate-600">{phase.description}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(phase.route)}
                  className={`px-4 py-3 rounded-lg font-medium text-sm md:text-base min-h-[44px] ${
                    phase.completed 
                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {phase.completed ? 'Review' : phase.id === 1 ? 'Begin Assessment' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-6 bg-slate-50 rounded-lg">
        <h3 className="font-semibold text-slate-900 mb-3">Why This Approach?</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
          <div>
            <h4 className="font-medium text-slate-800 mb-2">Browser Claude (Phases 2-4)</h4>
            <ul className="space-y-1">
              <li>• Strategic positioning and frameworks</li>
              <li>• Complex reasoning and analysis</li>
              <li>• Full parent control and refinement</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-slate-800 mb-2">API AI (Phase 5)</h4>
            <ul className="space-y-1">
              <li>• Personalized follow-up questions</li>
              <li>• Real-time response analysis</li>
              <li>• Seamless student experience</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};


const CollegeManagementTab = ({ 
  selectedChild, 
  parentColleges, 
  studentColleges, 
  loading, 
  onReload, 
  setShowAIPromptGen,
  selectedColleges,
  setSelectedColleges
}) => {
  const { family, currentUser } = useFamilyContext();
  const { updateCollege } = useCollegeContext();
  const navigate = useNavigate();
  const [addingCollege, setAddingCollege] = useState(false);
  const [showAIImporter, setShowAIImporter] = useState(false);
  const [newCollege, setNewCollege] = useState({
    name: '',
    location: '',
    category: 'target',
    notes: ''
  });

  const handleAddCollege = async () => {
    if (!newCollege.name.trim() || !selectedChild) return;

    try {
      await collegeJourneyService.addParentSuggestedCollege(
        {
          name: newCollege.name,
          location: newCollege.location,
          category: newCollege.category
        },
        currentUser.id,
        selectedChild.id,
        family.id,
        newCollege.notes
      );

      setNewCollege({ name: '', location: '', category: 'target', notes: '' });
      setAddingCollege(false);
      onReload();
    } catch (error) {
      console.error('Error adding college:', error);
      alert('Failed to add college: ' + error.message);
    }
  };

  // Handle AI data import
  const handleAIImport = async (importResults) => {
    try {
      let successCount = 0;
      
      for (const result of importResults) {
        if (result.isNewCollege) {
          // Add new college
          console.log('🆕 Adding new college:', result.collegeName);
          await addCollege(result.collegeData);
        } else {
          // Update existing college
          console.log('📝 Updating existing college:', result.collegeName);
          await updateCollege(result.collegeId, result.updates, currentUser.id, true);
        }
        successCount++;
      }
      
      onReload();
      return successCount;
    } catch (error) {
      console.error('Error importing AI data:', error);
      throw error;
    }
  };

  if (!selectedChild) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 text-center">
        <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2">Select a Student</h3>
        <p className="text-slate-600 text-sm md:text-base">Choose a student from the sidebar to manage their colleges</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-slate-900">College List Management</h2>
            <p className="text-slate-600 mt-1 text-sm md:text-base">Add and manage college suggestions for {selectedChild.displayName || selectedChild.name}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <button
              onClick={() => setShowAIPromptGen(true)}
              disabled={!selectedColleges.length}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-3 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px] text-sm md:text-base"
              aria-label="Generate AI Prompt"
            >
              <Star className="w-4 h-4" />
              AI Prompt Gen
            </button>
            <button
              onClick={() => setShowAIImporter(true)}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-3 font-medium transition focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px] text-sm md:text-base"
              aria-label="Import with AI"
            >
              <Sparkles className="w-4 h-4" />
              AI Import
            </button>
            <button
              onClick={() => setAddingCollege(!addingCollege)}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-3 font-medium transition focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px] text-sm md:text-base"
              aria-label="Add College"
            >
              <Plus className="w-4 h-4" />
              Add College
            </button>
            <button
              onClick={() => navigate('/college/colleges')}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-3 font-medium transition focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px] text-sm md:text-base"
              aria-label="College Database"
            >
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">College Database</span>
              <span className="sm:hidden">Database</span>
            </button>
          </div>
        </div>
      </div>

      {/* Add College Form */}
      {addingCollege && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-4">Add New College</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">College Name *</label>
              <input
                type="text"
                value={newCollege.name}
                onChange={(e) => setNewCollege(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Duke University"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
              <input
                type="text"
                value={newCollege.location}
                onChange={(e) => setNewCollege(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Durham, NC"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <select
                value={newCollege.category}
                onChange={(e) => setNewCollege(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="safety">Safety</option>
                <option value="target">Target</option>
                <option value="reach">reach</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
            <textarea
              value={newCollege.notes}
              onChange={(e) => setNewCollege(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm md:text-base"
              rows="3"
              placeholder="Why you're suggesting this college..."
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAddCollege}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-lg font-medium transition-colors min-h-[44px] text-sm md:text-base"
            >
              Add College
            </button>
            <button
              onClick={() => setAddingCollege(false)}
              className="bg-slate-300 hover:bg-slate-400 text-slate-700 px-4 py-3 rounded-lg font-medium transition-colors min-h-[44px] text-sm md:text-base"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Parent-Suggested Colleges */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 md:p-6 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-base md:text-lg font-semibold text-slate-900">Your College Suggestions</h3>
            <div className="flex items-center gap-4">
              {parentColleges.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedColleges.length === parentColleges.length && parentColleges.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedColleges(parentColleges.map(c => c.id));
                      } else {
                        setSelectedColleges([]);
                      }
                    }}
                    className="rounded border-slate-300 text-azure-600 focus:ring-azure-500"
                  />
                  <span className="text-sm text-slate-600">Select All</span>
                </div>
              )}
              <span className="text-sm text-slate-600">
                {parentColleges.length} colleges
                {selectedColleges.length > 0 && ` • ${selectedColleges.length} selected`}
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
              <p className="text-slate-600">Loading colleges...</p>
            </div>
          ) : parentColleges.length === 0 ? (
            <div className="text-center py-8">
              <GraduationCap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-slate-900 mb-2">No colleges added yet</h4>
              <p className="text-slate-600">Start building a college list for {selectedChild.displayName || selectedChild.name}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {parentColleges.map((college) => {
                const isSelected = selectedColleges.includes(college.id);
                // Tag color logic
                let categoryColor = 'bg-slate-900 text-white';
                if (college.category === 'reach' || college.category === 'reach') categoryColor = 'bg-[#FF6B6B] text-white';
                else if (college.category === 'target') categoryColor = 'bg-slate-900 text-white';
                else if (college.category === 'safety') categoryColor = 'bg-[#16A085] text-white';
                // Early Action tag logic (placeholder: show if college.early_action is true)
                const showEarlyAction = college.early_action;
                return (
                  <div
                    key={college.id}
                    className={`relative rounded-xl border p-6 mb-0 transition shadow-sm flex flex-col gap-3
                      ${isSelected ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white'}
                      hover:border-slate-900 focus-within:border-slate-900`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Custom Checkbox */}
                      <button
                        type="button"
                        aria-label={isSelected ? `Deselect ${college.name}` : `Select ${college.name}`}
                        onClick={() => {
                          if (isSelected) setSelectedColleges(selectedColleges.filter(id => id !== college.id));
                          else setSelectedColleges([...selectedColleges, college.id]);
                        }}
                        className={`mt-1 flex items-center justify-center w-7 h-7 rounded-md border-2 transition
                          ${isSelected ? 'bg-teal-500 border-teal-500' : 'bg-white border-slate-300'}
                          focus:outline-none focus:ring-2 focus:ring-teal-500`}
                        tabIndex={0}
                      >
                        {isSelected ? (
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        ) : (
                          <span className="block w-5 h-5" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="font-bold text-lg text-slate-900">{college.name}</div>
                        <div className="flex items-center gap-1 text-slate-600 text-sm mt-1">
                          <MapPin className="w-4 h-4" />
                          {college.location}
                        </div>
                        <div className="flex gap-3 mt-4 flex-wrap">
                          <span className={`rounded-md px-4 py-2 font-bold text-xs uppercase ${categoryColor}`}>{college.category === 'reach' ? 'REACH' : college.category?.toUpperCase()}</span>
                          {showEarlyAction && (
                            <span className="rounded-md px-4 py-2 font-bold text-xs uppercase bg-[#FFD166] text-slate-900 border border-yellow-400">EARLY ACTION</span>
                          )}
                          <span className="rounded-md px-4 py-2 font-bold text-xs uppercase bg-[#E5E5E5] text-slate-800 border border-slate-300">PARENT ADDED</span>
                        </div>
                      </div>
                      {/* Edit Icon Button */}
                      <button
                        type="button"
                        aria-label={`Edit ${college.name}`}
                        onClick={() => navigate(`/college/${college.id}`)}
                        className="ml-2 mt-1 flex items-center justify-center w-9 h-9 rounded-lg bg-slate-900 hover:bg-slate-700 transition focus:outline-none focus:ring-2 focus:ring-slate-900"
                        tabIndex={0}
                      >
                        <Edit3 className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Student's Colleges (Read-Only) */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Student's Own Colleges</h3>
            <div className="flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Read-only view</span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {studentColleges.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-slate-900 mb-2">No student colleges yet</h4>
              <p className="text-slate-600">{selectedChild.displayName || selectedChild.name} hasn't added any colleges on their own</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {studentColleges.map((college) => (
                <div 
                  key={college.id} 
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/college/${college.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{college.name}</h4>
                      <p className="text-sm text-slate-600">{college.location}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          college.category === 'reach' ? 'bg-red-100 text-red-700' :
                          college.category === 'target' ? 'bg-azure-100 text-azure-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {college.category}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-stone-100 text-slate-700">
                          Student Added
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`View ${college.name}`}
                      className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-900 hover:bg-slate-700 transition focus:outline-none focus:ring-2 focus:ring-slate-900"
                      tabIndex={0}
                    >
                      <Edit3 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AI Data Importer - Archived */}
      {/* {showAIImporter && (
        <AIDataImporter
          colleges={[...parentColleges, ...studentColleges]}
          onImport={handleAIImport}
          onClose={() => setShowAIImporter(false)}
        />
      )} */}
    </div>
  );
};

// Overview Tab (from ParentCollegeSetup)
const OverviewTab = ({ onTabChange }) => {
  const setupSteps = [
    {
      id: 'strategic-assessment',
      title: 'Strategic Assessment Framework',
      description: 'Set up the streamlined strategic assessment system with comprehensive student intelligence',
      status: 'pending',
      estimatedTime: '8 minutes'
    },
    {
      id: 'testing',
      title: 'Testing Hub Setup',
      description: 'Configure testing and refinement tools for the strategic assessment framework',
      status: 'pending', 
      estimatedTime: '5 minutes'
    },
    {
      id: 'colleges',
      title: 'College Suggestions & Research',
      description: 'Research and add initial college suggestions based on student profile and interests',
      status: 'pending', 
      estimatedTime: '25 minutes'
    },
    {
      id: 'timeline',
      title: 'Timeline & Deadlines',
      description: 'Set up key dates, milestones, and application deadlines for the college journey',
      status: 'pending',
      estimatedTime: '10 minutes'
    }
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-8">
      <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-900 mb-2">College Planning Setup</h2>
        <div className="text-sm text-slate-600">
          Prepare a comprehensive foundation for your student's college journey. Complete these setup steps 
          to create a strategic framework for college planning discussions and decision-making.
        </div>
      </div>


      {/* Setup Steps */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Setup Steps</h3>
        <div className="space-y-4">
          {setupSteps.map((step, index) => (
            <div key={step.id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-semibold text-slate-700">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-slate-900">{step.title}</h4>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      {step.estimatedTime}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
                <button 
                  onClick={() => onTabChange(step.id)}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Start
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Setup Summary */}
        <div className="mt-8 bg-azure-50 rounded-lg p-6">
          <h3 className="font-semibold text-azure-900 mb-2">Setup Summary</h3>
          <div className="text-sm text-azure-800 space-y-1">
            <p>• <strong>Total estimated time:</strong> ~65 minutes</p>
            <p>• <strong>Recommended approach:</strong> Complete steps in any order that works for your family</p>
            <p>• <strong>Goal:</strong> Create a comprehensive foundation before student begins their college journey</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Tab (Comprehensive version from ParentCollegeSetup)
const ProfileTab = ({ selectedChild, family }) => {
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'success', 'error'
  const [saveMessage, setSaveMessage] = useState('');
  
  const [profile, setProfile] = useState({
    // Basic Info
    graduationYear: '',
    highSchool: '',
    state: '',
    intendedMajor: '',
    
    // GPA Information
    currentGPA: '',
    weightedGPA: '',
    gpaScale: '4.0',
    classRank: '',
    classSize: '',
    
    // Test Scores
    satTotal: '',
    satMath: '',
    satERW: '', // Evidence-Based Reading and Writing
    actComposite: '',
    actEnglish: '',
    actMath: '',
    actReading: '',
    actScience: '',
    
    // Additional Tests
    psatTotal: '',
    psatDate: '',
    nationalMeritStatus: '',
    
    // IB Program Information
    ibDiplomaCandidate: false,
    ibPredictedScore: '',
    curriculumRigor: '',
    
    // Academic Interests & Goals
    academicInterests: '',
    careerGoals: '',
    intendedMinor: '',
    
    // Background & Special Circumstances
    firstGenerationCollege: false,
    underrepresentedBackground: false,
    legacyConnections: '',
    recruitedAthleteSports: '',
    specialCircumstances: '',
    
    // High School Details
    highSchoolState: '',
    highSchoolType: '',
    courseLoadDescription: '',
    
    // Coursework
    apCoursesTaken: '',
    apCoursesPlanned: '',
    apExamsPassed: '',
    apCourses: '',
    honorsCourses: '',
    dualEnrollmentCredits: '',
    dualEnrollment: '',
    
    // Extracurriculars & Activities
    extracurriculars: '',
    leadership: '',
    volunteering: '',
    workExperience: '',
    
    // Awards & Recognition
    academicAwards: '',
    athleticAwards: '',
    otherAwards: '',
    
    // Additional Information
    specialTalents: '',
    languages: '',
    notes: '',
    
    // Parent Observations
    parentObservationEasySubject: '',
    parentObservationGrowth: '',
    parentObservationPoorFit: '',
    parentObservationSurprises: '',
    parentObservationAgeLimit: '',
    parentObservationRulesQuestioned: '',
    parentObservationWrongLevel: '',
    parentObservationSelfTaught: '',
    parentObservationObsessions: '',
    parentObservationUnusualInterests: '',
    parentObservationHiddenQualities: ''
  });

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const updateProfile = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // Load existing profile data when selectedChild changes
  useEffect(() => {
    const loadExistingProfile = async () => {
      if (!selectedChild) return;
      
      console.log('🔄 ProfileTab: Loading existing profile for child:', selectedChild);
      
      try {
        // Try different possible ID field names for the child
        const childAuthUserId = selectedChild.auth_user_id || selectedChild.authUserId || selectedChild.user_id;
        const familyId = family?.id;
        
        console.log('🔄 ProfileTab: Searching for profile with user_id:', childAuthUserId);
        
        if (!childAuthUserId && !familyId) {
          console.log('🔄 ProfileTab: No valid IDs found for profile lookup');
          return;
        }
        
        // First try to find by auth user ID
        let query = supabase
          .from('student_academic_profiles')
          .select('*');
          
        if (childAuthUserId) {
          query = query.eq('user_id', childAuthUserId);
        } else {
          // If no auth user ID, cannot lookup profile
          console.log('🔄 ProfileTab: No auth user ID available for profile lookup');
          return;
        }
        
        const { data: profiles, error } = await query;
        
        if (error) {
          console.error('❌ Error loading profile:', error);
          return;
        }
        
        console.log('🔄 ProfileTab: Found profiles:', profiles);
        
        if (profiles && profiles.length > 0) {
          const existingProfile = profiles[0]; // Take the first one if multiple
          console.log('✅ ProfileTab: Loading existing profile data:', existingProfile);
          
          // Map database fields to form fields
          setProfile({
            // Basic Info
            graduationYear: existingProfile.graduation_year?.toString() || '',
            highSchool: existingProfile.high_school_name || '',
            state: existingProfile.state_of_residency || '',
            intendedMajor: existingProfile.intended_major || '',
            
            // GPA Information  
            currentGPA: existingProfile.gpa_unweighted?.toString() || '',
            weightedGPA: existingProfile.gpa_weighted?.toString() || '',
            gpaScale: existingProfile.gpa_scale?.toString() || '4.0',
            classRank: existingProfile.class_rank?.toString() || '',
            classSize: existingProfile.class_size?.toString() || '',
            
            // Test Scores
            satTotal: existingProfile.sat_total?.toString() || '',
            satMath: existingProfile.sat_math?.toString() || '',
            satERW: existingProfile.sat_reading_writing?.toString() || '',
            actComposite: existingProfile.act_composite?.toString() || '',
            actEnglish: existingProfile.act_english?.toString() || '',
            actMath: existingProfile.act_math?.toString() || '',
            actReading: existingProfile.act_reading?.toString() || '',
            actScience: existingProfile.act_science?.toString() || '',
            
            // Additional Tests
            psatTotal: existingProfile.psat_total?.toString() || '',
            psatDate: existingProfile.psat_date || '',
            nationalMeritStatus: existingProfile.national_merit_status || '',
            
            // IB Program Information
            ibDiplomaCandidate: existingProfile.ib_diploma_candidate || false,
            ibPredictedScore: existingProfile.ib_predicted_score?.toString() || '',
            curriculumRigor: existingProfile.curriculum_rigor || '',
            
            // Academic Interests & Goals
            academicInterests: existingProfile.academic_interests?.join(', ') || '',
            careerGoals: existingProfile.career_goals || '',
            intendedMinor: existingProfile.intended_minor || '',
            
            // Background & Special Circumstances
            firstGenerationCollege: existingProfile.first_generation_college || false,
            underrepresentedBackground: existingProfile.underrepresented_background || false,
            legacyConnections: existingProfile.legacy_connections?.join(', ') || '',
            specialCircumstances: existingProfile.special_circumstances || '',
            
            // High School Details
            highSchoolState: existingProfile.high_school_state || '',
            highSchoolType: existingProfile.high_school_type || '',
            courseLoadDescription: existingProfile.course_load_description || '',
            
            // Coursework
            apCoursesTaken: existingProfile.ap_courses_taken?.toString() || '',
            apCoursesPlanned: existingProfile.ap_courses_planned?.toString() || '',
            apExamsPassed: existingProfile.ap_exams_passed?.toString() || '',
            apCourses: existingProfile.ap_courses_details || '',
            honorsCourses: existingProfile.honors_courses?.toString() || '',
            dualEnrollmentCredits: existingProfile.dual_enrollment_credits?.toString() || '',
            dualEnrollment: '',
            
            // Extracurriculars & Activities - now consolidated in main table
            extracurriculars: existingProfile.extracurriculars || '',
            leadership: existingProfile.leadership_activities || '',
            volunteering: existingProfile.volunteer_work || '',
            workExperience: existingProfile.work_experience || '',
            
            // Awards & Recognition - now consolidated in main table
            academicAwards: existingProfile.academic_awards || '',
            // High School Context
            hsCompetitiveness: existingProfile.hs_competitiveness || '',
            hsAvgTestScores: existingProfile.hs_avg_test_scores || '',
            hsPctCollegeBound: existingProfile.hs_pct_college_bound?.toString() || '',
            
            // Grade Context  
            gradeTrend: existingProfile.grade_trend || '',
            gradeContext: existingProfile.grade_context || '',
            
            // Additional Information
            specialTalents: existingProfile.special_talents || '',
            languages: existingProfile.languages || '',
            notes: existingProfile.additional_notes || '',
            

            
            // Test dates and attempts
            
            // Parent Observations - now consolidated in main table
            parentObservationEasySubject: existingProfile.parent_observation_easy_subject || '',
            parentObservationGrowth: existingProfile.parent_observation_growth || '',
            parentObservationPoorFit: existingProfile.parent_observation_poor_fit || '',
            parentObservationSurprises: existingProfile.parent_observation_surprises || '',
            parentObservationAgeLimit: existingProfile.parent_observation_age_limit || '',
            parentObservationRulesQuestioned: existingProfile.parent_observation_rules_questioned || '',
            parentObservationWrongLevel: existingProfile.parent_observation_wrong_level || '',
            parentObservationSelfTaught: existingProfile.parent_observation_self_taught || '',
            parentObservationObsessions: existingProfile.parent_observation_obsessions || '',
            parentObservationUnusualInterests: existingProfile.parent_observation_unusual_interests || '',
            parentObservationHiddenQualities: existingProfile.parent_observation_hidden_qualities || ''
          });
          
          console.log('✅ ProfileTab: Profile data loaded successfully');
        } else {
          console.log('🔄 ProfileTab: No existing profile found, keeping empty form');
        }
      } catch (error) {
        console.error('❌ ProfileTab: Error loading existing profile:', error);
      }
    };
    
    loadExistingProfile();
  }, [selectedChild, family]);

  const saveProfile = async () => {
    if (!selectedChild) return;
    
    console.log('💾 ProfileTab: Starting profile save...');
    console.log('💾 ProfileTab: Selected child:', selectedChild);
    console.log('💾 ProfileTab: Family object:', family);
    console.log('💾 ProfileTab: Family ID from context:', family?.id);
    const { data: authData } = await supabase.auth.getUser();
    console.log('💾 ProfileTab: Current auth user:', authData?.user?.id);
    
    // Check family membership for RLS debugging
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('*');
    console.log('💾 ProfileTab: Family members:', familyMembers);
    
    // Resolve IDs - need to ensure we use the actual auth user ID, not family member ID
    console.log('💾 Available child fields:', Object.keys(selectedChild));
    console.log('💾 selectedChild.id:', selectedChild.id);
    console.log('💾 selectedChild.auth_user_id:', selectedChild.auth_user_id);
    console.log('💾 selectedChild.user_id:', selectedChild.user_id);
    console.log('💾 selectedChild.authUserId:', selectedChild.authUserId);
    
    // The user_id field must reference auth.users(id), so we need the actual auth user ID
    const childAuthUserId = selectedChild.auth_user_id || selectedChild.authUserId || selectedChild.user_id;
    const familyId = family?.id;
    
    console.log('💾 Resolved childAuthUserId (for auth.users):', childAuthUserId);
    console.log('💾 Resolved familyId:', familyId);
    
    if (!childAuthUserId || !familyId) {
      console.error('❌ ProfileTab: Missing IDs - childAuthUserId:', childAuthUserId, 'familyId:', familyId);
      setSaveStatus('error');
      setSaveMessage('Missing student auth user ID or family ID. Please refresh and try again.');
      setTimeout(() => { setSaveStatus(null); setSaveMessage(''); }, 5000);
      return;
    }
    
    setSaveStatus('saving');
    setSaveMessage('');
    
    try {
      console.log('💾 Saving profile for child auth user:', childAuthUserId, 'family:', familyId);
      console.log('💾 Profile data:', profile);
      
      // Check if child has an auth account, if not use parent's account as proxy
      let profileUserId = childAuthUserId;
      let isProxyProfile = false;
      
      if (!childAuthUserId) {
        console.log('🔄 Child has no auth user ID, using parent as proxy profile owner');
        profileUserId = authData?.user?.id;
        isProxyProfile = true;
      }
      
      console.log('💾 Final profile user ID:', profileUserId, 'isProxy:', isProxyProfile);
      
      // Prepare the database payload
      const dbPayload = {
        user_id: profileUserId,
        // Store child information if this is a proxy profile
        ...(isProxyProfile && {
          // We can store child info in a JSON field or create additional fields
          // For now, we'll add it to special_circumstances as metadata
          special_circumstances: `Profile managed by parent for child: ${selectedChild.displayName || selectedChild.name}. Child ID: ${selectedChild.id}`
        }),
        // Map form data to database schema
        graduation_year: profile.graduationYear ? parseInt(profile.graduationYear) : null,
        high_school_name: profile.highSchool || null,
        state_of_residency: profile.state || null,
        intended_major: profile.intendedMajor || null,
        gpa_weighted: profile.weightedGPA ? parseFloat(profile.weightedGPA) : null,
        gpa_unweighted: profile.currentGPA ? parseFloat(profile.currentGPA) : null,
        gpa_scale: profile.gpaScale ? parseFloat(profile.gpaScale) : 4.0,
        class_rank: profile.classRank ? parseInt(profile.classRank) : null,
        class_size: profile.classSize ? parseInt(profile.classSize) : null,
        sat_total: profile.satTotal ? parseInt(profile.satTotal) : null,
        sat_math: profile.satMath ? parseInt(profile.satMath) : null,
        sat_reading_writing: profile.satERW ? parseInt(profile.satERW) : null,
        act_composite: profile.actComposite ? parseInt(profile.actComposite) : null,
        act_math: profile.actMath ? parseInt(profile.actMath) : null,
        act_reading: profile.actReading ? parseInt(profile.actReading) : null,
        act_english: profile.actEnglish ? parseInt(profile.actEnglish) : null,
        act_science: profile.actScience ? parseInt(profile.actScience) : null,
        psat_total: profile.psatTotal ? parseInt(profile.psatTotal) : null,
        national_merit_status: profile.nationalMeritStatus || null,
        ap_courses_taken: profile.apCoursesTaken ? parseInt(profile.apCoursesTaken) : 0,
        ap_courses_planned: profile.apCoursesPlanned ? parseInt(profile.apCoursesPlanned) : 0,
        ap_exams_passed: profile.apExamsPassed ? parseInt(profile.apExamsPassed) : 0,
        honors_courses: profile.honorsCourses ? parseInt(profile.honorsCourses) : 0,
        dual_enrollment_credits: profile.dualEnrollmentCredits ? parseInt(profile.dualEnrollmentCredits) : 0,
        ib_diploma_candidate: profile.ibDiplomaCandidate || false,
        ib_predicted_score: profile.ibPredictedScore ? parseInt(profile.ibPredictedScore) : null,
        curriculum_rigor: profile.curriculumRigor || null,
        course_load_description: profile.courseLoadDescription || null,
        // Academic Interests & Goals
        academic_interests: profile.academicInterests ? profile.academicInterests.split(',').map(s => s.trim()).filter(s => s) : null,
        career_goals: profile.careerGoals || null,
        intended_minor: profile.intendedMinor || null,
        // High School Details
        high_school_state: profile.highSchoolState || null,
        high_school_type: profile.highSchoolType || null,
        // Background & Special Circumstances
        first_generation_college: profile.firstGenerationCollege || false,
        underrepresented_background: profile.underrepresentedBackground || false,
        legacy_connections: profile.legacyConnections ? profile.legacyConnections.split(',').map(s => s.trim()).filter(s => s) : null,
        special_circumstances: profile.specialCircumstances || null,
        // Additional consolidated fields
        ap_courses_details: profile.apCourses || null,
        extracurriculars: profile.extracurriculars || null,
        leadership_activities: profile.leadership || null,
        volunteer_work: profile.volunteering || null,
        work_experience: profile.workExperience || null,
        academic_awards: profile.academicAwards || null,
        // High School Context
        hs_competitiveness: profile.hsCompetitiveness || null,
        hs_avg_test_scores: profile.hsAvgTestScores || null,
        hs_pct_college_bound: profile.hsPctCollegeBound ? parseInt(profile.hsPctCollegeBound) : null,
        
        // Grade Context  
        grade_trend: profile.gradeTrend || null,
        grade_context: profile.gradeContext || null,
        special_talents: profile.specialTalents || null,
        languages: profile.languages || null,
        additional_notes: profile.notes || null,
        // Test dates (if available in form)
        // Parent observation fields
        parent_observation_easy_subject: profile.parentObservationEasySubject || null,
        parent_observation_growth: profile.parentObservationGrowth || null,
        parent_observation_poor_fit: profile.parentObservationPoorFit || null,
        parent_observation_surprises: profile.parentObservationSurprises || null,
        parent_observation_age_limit: profile.parentObservationAgeLimit || null,
        parent_observation_rules_questioned: profile.parentObservationRulesQuestioned || null,
        parent_observation_wrong_level: profile.parentObservationWrongLevel || null,
        parent_observation_self_taught: profile.parentObservationSelfTaught || null,
        parent_observation_obsessions: profile.parentObservationObsessions || null,
        parent_observation_unusual_interests: profile.parentObservationUnusualInterests || null,
        parent_observation_hidden_qualities: profile.parentObservationHiddenQualities || null,
        last_updated: new Date().toISOString()
      };
      
      console.log('💾 Database payload:', dbPayload);
      
      // Check if a profile already exists
      const { data: existingProfiles } = await supabase
        .from('student_academic_profiles')
        .select('id')
        .eq('user_id', profileUserId);
      
      let data, error;
      
      if (existingProfiles && existingProfiles.length > 0) {
        // Update existing profile
        console.log('💾 Updating existing profile with ID:', existingProfiles[0].id);
        const updateResult = await supabase
          .from('student_academic_profiles')
          .update(dbPayload)
          .eq('id', existingProfiles[0].id)
          .select()
          .single();
        data = updateResult.data;
        error = updateResult.error;
      } else {
        // Create new profile
        console.log('💾 Creating new profile');
        const insertResult = await supabase
          .from('student_academic_profiles')
          .insert(dbPayload)
          .select()
          .single();
        data = insertResult.data;
        error = insertResult.error;
      }
      
      if (error) throw error;
      
      setSaveStatus('success');
      setSaveMessage(`Profile saved successfully for ${selectedChild.displayName || selectedChild.name}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus(null);
        setSaveMessage('');
      }, 3000);
      
      console.log('✅ Profile saved:', data);
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error keys:', Object.keys(error));
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error details:', error?.details);
      console.error('❌ Error hint:', error?.hint);
      console.error('❌ Error code:', error?.code);
      
      setSaveStatus('error');
      
      // Better error message handling
      let errorMessage = 'Failed to save profile';
      if (error?.message) {
        errorMessage += ': ' + error.message;
      } else if (error?.details) {
        errorMessage += ': ' + error.details;
      } else if (error?.hint) {
        errorMessage += ': ' + error.hint;
      } else if (typeof error === 'string') {
        errorMessage += ': ' + error;
      } else {
        errorMessage += ': Unknown error occurred';
        console.error('❌ Full error object:', JSON.stringify(error, null, 2));
      }
      
      setSaveMessage(errorMessage);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveStatus(null);
        setSaveMessage('');
      }, 5000);
    }
  };

  if (!selectedChild) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Student Selected</h3>
        <p className="text-slate-600">Please select a student from the sidebar to set up their profile.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-xl font-bold text-slate-900">
          Academic Profile for {selectedChild.displayName || selectedChild.name}
        </h2>
        <p className="text-slate-600 mt-1">
          Provide comprehensive academic information that will help with college planning
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Graduation Year *
              </label>
              <select
                value={profile.graduationYear}
                onChange={(e) => updateProfile('graduationYear', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select year...</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
                <option value="2028">2028</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                State *
              </label>
              <select
                value={profile.state}
                onChange={(e) => updateProfile('state', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select state...</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* High School Information */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">High School Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                High School Name
              </label>
              <input
                type="text"
                value={profile.highSchool}
                onChange={(e) => updateProfile('highSchool', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Lincoln High School"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Intended Major(s)
              </label>
              <input
                type="text"
                value={profile.intendedMajor}
                onChange={(e) => updateProfile('intendedMajor', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Political Science, Engineering, Undecided..."
              />
            </div>
          </div>
        </div>

        {/* GPA Information */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">GPA & Class Rank</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Unweighted GPA
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  step="0.01"
                  value={profile.currentGPA}
                  onChange={(e) => updateProfile('currentGPA', e.target.value)}
                  className="flex-1 px-2 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="3.85"
                />
                <select
                  value={profile.gpaScale}
                  onChange={(e) => updateProfile('gpaScale', e.target.value)}
                  className="w-16 px-1 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="4.0">4.0</option>
                  <option value="5.0">5.0</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Weighted GPA
              </label>
              <input
                type="number"
                step="0.01"
                value={profile.weightedGPA}
                onChange={(e) => updateProfile('weightedGPA', e.target.value)}
                className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm"
                placeholder="4.25"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Class Rank
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  value={profile.classRank}
                  onChange={(e) => updateProfile('classRank', e.target.value)}
                  className="flex-1 px-2 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="15"
                />
                <span className="px-1 py-2 text-slate-500 text-xs flex items-center">of</span>
                <input
                  type="number"
                  value={profile.classSize}
                  onChange={(e) => updateProfile('classSize', e.target.value)}
                  className="flex-1 px-2 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="400"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SAT Scores */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">SAT Scores</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Total Score
              </label>
              <input
                type="number"
                value={profile.satTotal}
                onChange={(e) => updateProfile('satTotal', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="1480"
                min="400"
                max="1600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Math Section
              </label>
              <input
                type="number"
                value={profile.satMath}
                onChange={(e) => updateProfile('satMath', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="750"
                min="200"
                max="800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Evidence-Based Reading & Writing
              </label>
              <input
                type="number"
                value={profile.satERW}
                onChange={(e) => updateProfile('satERW', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="730"
                min="200"
                max="800"
              />
            </div>
          </div>
        </div>

        {/* ACT Scores */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">ACT Scores</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Composite
              </label>
              <input
                type="number"
                value={profile.actComposite}
                onChange={(e) => updateProfile('actComposite', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="32"
                min="1"
                max="36"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                English
              </label>
              <input
                type="number"
                value={profile.actEnglish}
                onChange={(e) => updateProfile('actEnglish', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="34"
                min="1"
                max="36"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Math
              </label>
              <input
                type="number"
                value={profile.actMath}
                onChange={(e) => updateProfile('actMath', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="31"
                min="1"
                max="36"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reading
              </label>
              <input
                type="number"
                value={profile.actReading}
                onChange={(e) => updateProfile('actReading', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="33"
                min="1"
                max="36"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Science
              </label>
              <input
                type="number"
                value={profile.actScience}
                onChange={(e) => updateProfile('actScience', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="30"
                min="1"
                max="36"
              />
            </div>
          </div>
        </div>

        {/* PSAT & Additional Test Scores */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">PSAT & Additional Test Scores</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                PSAT Total Score
              </label>
              <input
                type="number"
                value={profile.psatTotal || ''}
                onChange={(e) => updateProfile('psatTotal', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="1420"
                min="320"
                max="1520"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                PSAT Date
              </label>
              <input
                type="date"
                value={profile.psatDate || ''}
                onChange={(e) => updateProfile('psatDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                National Merit Status
              </label>
              <select
                value={profile.nationalMeritStatus || ''}
                onChange={(e) => updateProfile('nationalMeritStatus', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Not applicable</option>
                <option value="commended">Commended Scholar</option>
                <option value="semifinalist">Semifinalist</option>
                <option value="finalist">Finalist</option>
                <option value="scholar">National Merit Scholar</option>
              </select>
            </div>
          </div>
        </div>

        {/* IB Program Information */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">International Baccalaureate (IB)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                IB Diploma Candidate
              </label>
              <select
                value={profile.ibDiplomaCandidate || ''}
                onChange={(e) => updateProfile('ibDiplomaCandidate', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                IB Predicted Score
              </label>
              <input
                type="number"
                value={profile.ibPredictedScore || ''}
                onChange={(e) => updateProfile('ibPredictedScore', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="42"
                min="24"
                max="45"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Curriculum Rigor
              </label>
              <select
                value={profile.curriculumRigor || ''}
                onChange={(e) => updateProfile('curriculumRigor', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select rigor level</option>
                <option value="most_rigorous">Most Rigorous</option>
                <option value="very_rigorous">Very Rigorous</option>
                <option value="rigorous">Rigorous</option>
                <option value="moderately_rigorous">Moderately Rigorous</option>
                <option value="less_rigorous">Less Rigorous</option>
              </select>
            </div>
          </div>
        </div>

        {/* Academic Interests & Goals */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Academic Interests & Goals</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Academic Interests
              </label>
              <textarea
                value={profile.academicInterests || ''}
                onChange={(e) => updateProfile('academicInterests', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="STEM, Liberal Arts, Business, Fine Arts..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Career Goals
              </label>
              <textarea
                value={profile.careerGoals || ''}
                onChange={(e) => updateProfile('careerGoals', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="Software engineer, doctor, teacher, researcher..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Intended Minor
              </label>
              <input
                type="text"
                value={profile.intendedMinor || ''}
                onChange={(e) => updateProfile('intendedMinor', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Mathematics, Psychology, Music..."
              />
            </div>
          </div>
        </div>

        {/* Background & Special Circumstances */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Background & Special Circumstances</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                First Generation College Student
              </label>
              <select
                value={profile.firstGenerationCollege || ''}
                onChange={(e) => updateProfile('firstGenerationCollege', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">No</option>
                <option value="true">Yes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Underrepresented Background
              </label>
              <select
                value={profile.underrepresentedBackground || ''}
                onChange={(e) => updateProfile('underrepresentedBackground', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">No</option>
                <option value="true">Yes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Legacy Connections
              </label>
              <textarea
                value={profile.legacyConnections || ''}
                onChange={(e) => updateProfile('legacyConnections', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="2"
                placeholder="Parent attended Harvard, Sibling at Duke..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recruited Athlete Sports
              </label>
              <textarea
                value={profile.recruitedAthleteSports || ''}
                onChange={(e) => updateProfile('recruitedAthleteSports', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="2"
                placeholder="Basketball, Soccer, Swimming..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Special Circumstances
              </label>
              <textarea
                value={profile.specialCircumstances || ''}
                onChange={(e) => updateProfile('specialCircumstances', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="Family challenges, medical issues, unique background information..."
              />
            </div>
          </div>
        </div>

        {/* High School Details */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">High School Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                High School State
              </label>
              <select
                value={profile.highSchoolState || ''}
                onChange={(e) => updateProfile('highSchoolState', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select state...</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                High School Type
              </label>
              <select
                value={profile.highSchoolType || ''}
                onChange={(e) => updateProfile('highSchoolType', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select type...</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="charter">Charter</option>
                <option value="homeschool">Homeschool</option>
                <option value="international">International</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Course Load Description
              </label>
              <textarea
                value={profile.courseLoadDescription || ''}
                onChange={(e) => updateProfile('courseLoadDescription', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="2"
                placeholder="Most challenging available, mix of AP and honors..."
              />
            </div>
          </div>
        </div>

        {/* Coursework */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Coursework & Academic Rigor</h3>
          <div className="space-y-4">
            {/* AP Courses - Enhanced with numeric counts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  AP Courses Taken
                </label>
                <input
                  type="number"
                  value={profile.apCoursesTaken || ''}
                  onChange={(e) => updateProfile('apCoursesTaken', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="8"
                  min="0"
                  max="30"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  AP Courses Planned
                </label>
                <input
                  type="number"
                  value={profile.apCoursesPlanned || ''}
                  onChange={(e) => updateProfile('apCoursesPlanned', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="3"
                  min="0"
                  max="20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  AP Exams Passed (3+)
                </label>
                <input
                  type="number"
                  value={profile.apExamsPassed || ''}
                  onChange={(e) => updateProfile('apExamsPassed', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="6"
                  min="0"
                  max="30"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                AP Courses & Scores (Details)
              </label>
              <textarea
                value={profile.apCourses || ''}
                onChange={(e) => updateProfile('apCourses', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="AP Biology (5), AP Calculus BC (4), AP English Language (5)..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Honors Courses Count
                </label>
                <input
                  type="number"
                  value={profile.honorsCourses || ''}
                  onChange={(e) => updateProfile('honorsCourses', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="12"
                  min="0"
                  max="50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dual Enrollment Credits
                </label>
                <input
                  type="number"
                  value={profile.dualEnrollmentCredits || ''}
                  onChange={(e) => updateProfile('dualEnrollmentCredits', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="15"
                  min="0"
                  max="120"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Dual Enrollment/College Courses (Details)
              </label>
              <textarea
                value={profile.dualEnrollment || ''}
                onChange={(e) => updateProfile('dualEnrollment', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="College Algebra at Community College, Intro to Psychology..."
              />
            </div>
          </div>
        </div>

        {/* Extracurriculars & Activities */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Extracurricular Activities</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Clubs, Sports, Organizations
              </label>
              <textarea
                value={profile.extracurriculars}
                onChange={(e) => updateProfile('extracurriculars', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="4"
                placeholder="National Honor Society (2 years), Varsity Soccer (4 years), Robotics Club (3 years, President)..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Leadership Roles
                </label>
                <textarea
                  value={profile.leadership}
                  onChange={(e) => updateProfile('leadership', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows="3"
                  placeholder="Student Council Treasurer, Team Captain..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Volunteer Work
                </label>
                <textarea
                  value={profile.volunteering}
                  onChange={(e) => updateProfile('volunteering', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows="3"
                  placeholder="Local food bank (100+ hours), tutoring elementary students..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Work Experience
              </label>
              <textarea
                value={profile.workExperience}
                onChange={(e) => updateProfile('workExperience', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="Summer internship at tech company, part-time job at local restaurant..."
              />
            </div>
          </div>
        </div>

        {/* Awards & Recognition */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Awards & Recognition</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Academic Awards
              </label>
              <textarea
                value={profile.academicAwards}
                onChange={(e) => updateProfile('academicAwards', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="4"
                placeholder="Dean's List, National Merit Finalist, Math Olympiad 1st Place..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Athletic Awards
              </label>
              <textarea
                value={profile.athleticAwards}
                onChange={(e) => updateProfile('athleticAwards', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="4"
                placeholder="All-State Soccer, MVP Award, League Championship..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Other Awards & Recognition
              </label>
              <textarea
                value={profile.otherAwards}
                onChange={(e) => updateProfile('otherAwards', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="4"
                placeholder="Eagle Scout, Science Fair Regional Winner, Art Competition 2nd Place..."
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4">Additional Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Special Talents/Skills
                </label>
                <textarea
                  value={profile.specialTalents}
                  onChange={(e) => updateProfile('specialTalents', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows="3"
                  placeholder="Programming, Musical instruments, Art, Public speaking..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Languages Spoken
                </label>
                <textarea
                  value={profile.languages}
                  onChange={(e) => updateProfile('languages', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows="3"
                  placeholder="English (native), Spanish (fluent), French (conversational)..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={profile.notes}
                onChange={(e) => updateProfile('notes', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="Any other information that might be relevant for college applications..."
              />
            </div>
          </div>
        </div>

        {/* Enhanced Parent Observations - NEW SECTION */}
        <div className="bg-azure-50 border border-azure-200 rounded-lg p-6">
          <h3 className="font-semibold text-slate-900 mb-2">Parent Observations & Insights</h3>
          <p className="text-sm text-slate-600 mb-6">
            These questions help us understand what makes your child unique beyond grades and test scores. 
            Your honest observations will help create personalized guidance.
          </p>

          <div className="space-y-6">
            {/* Academic & Learning Profile */}
            <div>
              <h4 className="font-medium text-slate-800 mb-4">Academic & Learning Profile</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What subject does your child find surprisingly easy that others struggle with?
                  </label>
                  <textarea
                    value={profile.parentObservationEasySubject || ''}
                    onChange={(e) => updateProfile('parentObservationEasySubject', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., They grasp abstract math concepts immediately while classmates need multiple explanations..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Where have you seen the biggest academic growth or breakthrough?
                  </label>
                  <textarea
                    value={profile.parentObservationGrowth || ''}
                    onChange={(e) => updateProfile('parentObservationGrowth', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Struggled with writing until 10th grade, then suddenly started writing beautifully..."
                  />
                </div>
              </div>
            </div>

            {/* System Interactions & Edge Cases */}
            <div>
              <h4 className="font-medium text-slate-800 mb-4">System Interactions & Edge Cases</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Describe a time when school/traditional structures seemed like a poor fit for your child
                  </label>
                  <textarea
                    value={profile.parentObservationPoorFit || ''}
                    onChange={(e) => updateProfile('parentObservationPoorFit', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., The gifted program was too slow, regular classes too easy, no good fit anywhere..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What does your child do that consistently surprises adults (teachers, coaches, family friends)?
                  </label>
                  <textarea
                    value={profile.parentObservationSurprises || ''}
                    onChange={(e) => updateProfile('parentObservationSurprises', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Adults are shocked when they debate complex topics like an adult, or solve problems in unexpected ways..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?
                  </label>
                  <textarea
                    value={profile.parentObservationAgeLimit || ''}
                    onChange={(e) => updateProfile('parentObservationAgeLimit', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Wanted to take calculus in 9th grade but school said no, tried to join varsity as freshman..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What rules or systems has your child questioned that made you think 'actually, they have a point'?
                  </label>
                  <textarea
                    value={profile.parentObservationRulesQuestioned || ''}
                    onChange={(e) => updateProfile('parentObservationRulesQuestioned', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Questioned why they couldn't test out of required classes, why certain rules exist..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Has your child ever been placed in the 'wrong' level for something, either too easy or too hard?
                  </label>
                  <textarea
                    value={profile.parentObservationWrongLevel || ''}
                    onChange={(e) => updateProfile('parentObservationWrongLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Reading at college level but placed in grade-level English, or struggled in advanced math despite being capable..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What's something your child has taught themselves that surprised you?
                  </label>
                  <textarea
                    value={profile.parentObservationSelfTaught || ''}
                    onChange={(e) => updateProfile('parentObservationSelfTaught', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Learned programming from YouTube, taught themselves an instrument, mastered a skill without formal instruction..."
                  />
                </div>
              </div>
            </div>

            {/* Natural Tendencies & Uniqueness */}
            <div>
              <h4 className="font-medium text-slate-800 mb-4">Natural Tendencies & Uniqueness</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What does your child get obsessed with or spend way too much time on?
                  </label>
                  <textarea
                    value={profile.parentObservationObsessions || ''}
                    onChange={(e) => updateProfile('parentObservationObsessions', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Researches obscure historical events for hours, builds complex Minecraft worlds, reads everything about space..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What's something your child cares deeply about that doesn't seem typical for their age/peer group?
                  </label>
                  <textarea
                    value={profile.parentObservationUnusualInterests || ''}
                    onChange={(e) => updateProfile('parentObservationUnusualInterests', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="3"
                    placeholder="E.g., Deeply concerned about climate policy, fascinated by philosophy, passionate about social justice issues..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What do you hope college admissions officers will understand about your child that might not show up in grades/scores?
                  </label>
                  <textarea
                    value={profile.parentObservationHiddenQualities || ''}
                    onChange={(e) => updateProfile('parentObservationHiddenQualities', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                    rows="4"
                    placeholder="E.g., Their creativity isn't captured by standardized tests, they're a deep thinker who questions everything, they have unusual empathy..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-slate-200">
          {/* Success/Error Message */}
          {saveStatus && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              saveStatus === 'success' ? 'bg-emerald-50 border border-emerald-200' :
              saveStatus === 'error' ? 'bg-red-50 border border-red-200' :
              'bg-azure-50 border border-azure-200'
            }`}>
              {saveStatus === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
              {saveStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
              {saveStatus === 'saving' && <Clock className="w-5 h-5 text-azure-600 animate-spin" />}
              <span className={
                saveStatus === 'success' ? 'text-emerald-700' :
                saveStatus === 'error' ? 'text-red-700' :
                'text-azure-700'
              }>
                {saveMessage || (saveStatus === 'saving' ? 'Saving profile...' : '')}
              </span>
            </div>
          )}
          
          <button
            onClick={saveProfile}
            disabled={saveStatus === 'saving'}
            className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
          >
            {saveStatus === 'saving' ? (
              <>
                <Clock className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Timeline Tab
const TimelineTab = ({ selectedChild }) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-8">
      <h2 className="text-xl font-bold text-slate-900 mb-6">Timeline & Deadlines</h2>
      
      <div className="space-y-6">
        <div className="bg-slate-50 rounded-lg p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Key Dates
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-white rounded p-3">
              <div>
                <div className="font-medium">FAFSA Opens</div>
                <div className="text-sm text-slate-600">October 1, 2024</div>
              </div>
              <span className="text-sm text-emerald-600">Coming up</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded p-3">
              <div>
                <div className="font-medium">Early Decision Deadline</div>
                <div className="text-sm text-slate-600">November 1, 2024</div>
              </div>
              <span className="text-sm text-yellow-600">3 months</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded p-3">
              <div>
                <div className="font-medium">Regular Decision Deadline</div>
                <div className="text-sm text-slate-600">January 1, 2025</div>
              </div>
              <span className="text-sm text-slate-500">5 months</span>
            </div>
          </div>
        </div>

        <div className="text-center py-8">
          <button
            onClick={() => navigate('/college/timeline')}
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            View Full Timeline
          </button>
        </div>
      </div>
    </div>
  );
};

// Insights Tab
const InsightsTab = ({ selectedChild }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-8">
      <h2 className="text-xl font-bold text-slate-900 mb-6">Insights & Analytics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-50 rounded-lg p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-azure-600" />
            Application Progress
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Colleges Added</span>
              <span className="font-medium">8</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Essays Started</span>
              <span className="font-medium">0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Applications Submitted</span>
              <span className="font-medium">0</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Financial Overview
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Application Fees</span>
              <span className="font-medium">~$600</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>FAFSA Status</span>
              <span className="font-medium text-yellow-600">Not Started</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>CSS Profile</span>
              <span className="font-medium text-yellow-600">Not Started</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-azure-50 rounded-lg p-6">
        <h3 className="font-semibold text-azure-900 mb-2">Pro Tip</h3>
        <p className="text-sm text-azure-800">
          Start with safety schools first to build confidence, then work your way up to target and reach schools.
          Aim for a balanced list with 2-3 schools in each category.
        </p>
      </div>
    </div>
  );
};

// Testing Hub Tab
const TestingTab = () => {
  const navigate = useNavigate();

  // Navigate directly to testing hub
  React.useEffect(() => {
    navigate('/college/parent-testing');
  }, [navigate]);

  return null; // This component now just redirects
};

// Collaboration Tab - Parent-Student Notes and Communication
const CollaborationTab = ({ selectedChild, showKidView }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('suggestion');

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const note = {
      id: Date.now(),
      content: newNote,
      type: noteType,
      author: 'parent',
      timestamp: new Date().toISOString(),
      studentId: selectedChild?.id
    };

    setNotes(prev => [note, ...prev]);
    setNewNote('');
  };

  if (!selectedChild) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Select a Student</h3>
        <p className="text-slate-600">Choose a student to view collaboration notes</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Parent-Student Collaboration</h2>
          <p className="text-slate-600 mt-1">
            {showKidView 
              ? `What ${selectedChild.displayName || selectedChild.name} sees` 
              : `Notes and suggestions for ${selectedChild.displayName || selectedChild.name}`
            }
          </p>
        </div>
        {showKidView && (
          <div className="bg-azure-50 border border-azure-200 rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-azure-700">Student View</span>
          </div>
        )}
      </div>
      
      {!showKidView && (
        <div className="mb-6 bg-slate-50 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Add New Note</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="suggestion">Suggestion</option>
                <option value="reminder">Reminder</option>
                <option value="resource">Resource</option>
                <option value="encouragement">Encouragement</option>
              </select>
            </div>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note, suggestion, or resource for your student..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              rows="3"
            />
            <button
              onClick={handleAddNote}
              className="bg-azure-600 hover:bg-azure-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Add Note
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">
          {showKidView ? 'Messages from Parents' : 'Your Notes & Suggestions'}
        </h3>
        
        {notes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No collaboration notes yet</p>
            {!showKidView && (
              <p className="text-sm mt-1">Add notes above to get started</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div key={note.id} className={`p-4 rounded-lg border ${
                showKidView
                  ? 'bg-azure-50 border-azure-200'
                  : 'bg-white border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    note.type === 'suggestion' ? 'bg-emerald-100 text-emerald-700' :
                    note.type === 'reminder' ? 'bg-orange-100 text-orange-700' :
                    note.type === 'resource' ? 'bg-violet-100 text-violet-700' :
                    'bg-pink-100 text-pink-700'
                  }`}>
                    {note.type}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(note.timestamp).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-700">{note.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {showKidView && (
        <div className="mt-6 bg-azure-50 rounded-lg p-4 border border-azure-200">
          <p className="text-sm text-azure-800">
            <strong>Student Perspective:</strong> This is how collaborative notes and suggestions 
            appear to your student. They can see parent suggestions but cannot edit them.
          </p>
        </div>
      )}
    </div>
  );
};

// Access Control Tab Component
const AccessControlTab = ({ allChildren, moduleAccess, updateModuleAccess, isLoadingAccess, accessError }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          College Journey Access Control
        </h2>
        <p className="text-slate-600 mt-1">
          Manage which children can access the college journey hub and assessment tools
        </p>
        {accessError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <p className="text-sm font-medium">{accessError}</p>
            </div>
          </div>
        )}
      </div>

      {isLoadingAccess ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-slate-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
            <span>Loading access settings...</span>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {allChildren.map((child) => (
            <div key={child.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">
                    {child.displayName || child.name}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {child.email || `${child.type || 'Student'} account`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${
                  moduleAccess[child.id] !== false ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {moduleAccess[child.id] !== false ? 'Has Access' : 'No Access'}
                </span>
                <button
                  onClick={() => updateModuleAccess(child.id, moduleAccess[child.id] === false)}
                  disabled={isLoadingAccess}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    moduleAccess[child.id] !== false
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  }`}
                >
                  {moduleAccess[child.id] !== false ? 'Revoke Access' : 'Grant Access'}
                </button>
              </div>
            </div>
          ))}
          
          {allChildren.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No children found in your family</p>
              <p className="text-sm mt-1">Add children to your family to manage access</p>
            </div>
          )}
        </div>
      )}
      
      <div className="mt-6 bg-azure-50 border border-azure-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-azure-900">
            <p className="font-semibold mb-1">Access Control Notes</p>
            <ul className="space-y-1 text-azure-800">
              <li>• Children with access can view and take the college journey assessment</li>
              <li>• Revoking access will hide the module from their navigation</li>
              <li>• You can grant or revoke access at any time</li>
              <li>• All children have access by default when first added</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentCollegeHub;